import { Heading, Stack, Tag } from '@chakra-ui/react';
import { QuoteStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useCallback, useMemo } from 'react';
import { HiLink } from 'react-icons/hi2';
import { Link as RRLink } from 'react-router-dom';

import { DuplicateRequest } from '../hooks/request/useRelated';
import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { Quote, Request } from '../utils/types';

type Props = {
  related: DuplicateRequest[];
  limit?: number;
  isDuplicate?: boolean;
};

const DotAlert = ({ char }: { char: string }) => (
  <Tag
    borderRadius="full"
    colorScheme="red"
    justifyContent="center"
    minH="unset"
    minW="unset"
    // Needed to specify px explicity to get spacing right
    px="3.5px"
  >
    {char}
  </Tag>
);

const getSyncTags = (baseRequest: Request, baseQuotes: Quote[], dupeRequest: DuplicateRequest) => {
  const statusInSync = baseRequest.status === dupeRequest.status;
  const quotesCompletedBase = baseQuotes.reduce((count, cur) => {
    if (cur.status === QuoteStatus.InProgress) {
      count++;
    }
    return count;
  }, 0);
  const quotesCompletedDupe = dupeRequest.quotes.reduce((count, cur) => {
    if (cur.status === QuoteStatus.InProgress) {
      count++;
    }
    return count;
  }, 0);
  const quotesInSync = quotesCompletedBase === quotesCompletedDupe;
  return (
    <>
      <Show if={!statusInSync}>
        <DotAlert char="R" />
      </Show>
      <Show if={!quotesInSync}>
        <DotAlert char="Q" />
      </Show>
    </>
  );
};

export const RelatedRequests = ({ related, limit, isDuplicate }: Props) => {
  const { request, quotes } = useCurrentRequest();

  const renderTag = useCallback(
    (dupeRequest: DuplicateRequest) => {
      const isSameAccount = request?.account_id === dupeRequest?.account_id;
      const colorScheme = isSameAccount ? 'blue' : 'gray';
      const syncInfo = isDuplicate ? getSyncTags(request, quotes ?? [], dupeRequest) : <></>;
      const isMainRequest = !!dupeRequest?.linked_requests?.length;
      const isLinked = !!dupeRequest.main_request_id || isMainRequest;
      const linkColor = isMainRequest ? 'orange' : 'unset';

      return (
        <Tag
          as={RRLink}
          key={dupeRequest.id}
          target="_blank"
          to={`/requests/${dupeRequest.id}`}
          colorScheme={colorScheme}
          size="sm"
          display="flex"
          gap={1}
        >
          R{dupeRequest.id} {syncInfo} {isLinked && <HiLink color={linkColor} />}
        </Tag>
      );
    },
    [request, isDuplicate, quotes],
  );

  const requestsToRender = useMemo(() => {
    if (limit) return related.slice(0, limit);
    return related;
  }, [related, limit]);

  if (!related) return null;

  return (
    <Stack direction="column" spacing={1}>
      <Heading size="xs">{isDuplicate ? 'Duplicates' : 'Similar'}</Heading>
      <Stack direction="row" width="full" flexWrap="wrap">
        {requestsToRender?.map(renderTag)}
      </Stack>
    </Stack>
  );
};
